import {FC, useEffect, useState} from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Link, useLocation } from 'react-router-dom'
import {ConnectedPlatform} from 'src/interfaces/Platform.interface'
import {KTIcon, KTSVG} from 'src/marketConnect/helpers'
import { loggedInAdminAccessToken } from 'src/utils/constants'
import { useCustomerAccess } from 'src/hooks/useCustomerAccess'
import moment from 'moment'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import { getAllPlatformTypesAsync } from 'src/redux/actions/platformActions'
import { useUserHavingWriteAccess } from 'src/utils/useUserHavingWriteAccess'
import { usePermission } from 'src/hooks/usePermission'

type ConnectedPlatformStripe = {
  platform: ConnectedPlatform
  onDelete: () => void
  onTokenRefresh: ()=>void
  handleOauth: ()=>void
  isRefreshing:boolean
  connectedPlatformsTypewise? : any
}

const ConnectPlatformStrip: FC<ConnectedPlatformStripe> = ({
  platform,
  onDelete,
  onTokenRefresh,
  handleOauth,
  isRefreshing,
  connectedPlatformsTypewise
}) => {
  const {isRead, isWrite, isDelete} = usePermission();
  const location = useLocation()
  const hasRefreshExpiry = !!platform?.credentials?.refresh_token_expires_date;
  const expirationDate = hasRefreshExpiry ? moment(platform?.credentials?.refresh_token_expires_date) : null;
  const today = moment();
  const daysUntilExpiration = expirationDate ? expirationDate.diff(today, 'days') : null;
  
  const {mainStoreData} = useAppSelector((state) => state.fetchConnectedPlatform)

  const { fetchedUserAccountData } = useAppSelector(state=>state.userAccountData)
  const { isUserHavingWriteAccess } = useUserHavingWriteAccess();
  const customerAccess = useCustomerAccess();

  // Delete button visibility logic for Active Integration tab
  // When admin logs in as customer (parent_user_role exists), grant full customer access
  // This allows them to perform all customer actions including deleting all connections
  const canShowDeleteButton = () => {
    // Admin logged in as customer (parent_user_role exists) - grant full customer access
    // Allow delete for all connections, not just primary store
    if (fetchedUserAccountData?.parent_user_role) {
      return true
    }

    // Direct customer login (user_type === 'USER' and no parent_user_role) - allow delete
    if (fetchedUserAccountData?.user_type === 'USER' && !fetchedUserAccountData?.parent_user_role) {
      return true
    }

    // Direct admin login (no parent_user_role) - check permissions
    const roleToCheck = fetchedUserAccountData?.role
    const customerAccessLevel = roleToCheck?.customer_access?.toUpperCase()
    
    // Hide if RESTRICT or READ
    if (customerAccessLevel === 'RESTRICT' || customerAccessLevel === 'READ') {
      return false
    }

    // Allow for WRITE or SUPERADMIN
    return customerAccessLevel === 'WRITE' || customerAccessLevel === 'SUPERADMIN' || fetchedUserAccountData?.is_superuser
  }

  // Get delete button props with custom logic
  const getDeleteButtonProps = () => {
    const canDelete = canShowDeleteButton()
    
    if (!canDelete) {
      let title = ''
      if (customerAccess.isReadOnly) {
        title = 'Read-only access'
      } else if (customerAccess.isRestricted) {
        title = 'Access restricted'
      } else if (platform?.user && !customerAccess.hasAccessToCustomer(platform.user)) {
        title = 'No access to this customer'
      } else {
        title = 'Delete not permitted'
      }
      
      return {
        disabled: true,
        style: { opacity: 0.5, cursor: 'not-allowed' },
        title
      }
    }

    return {
      disabled: false,
      style: undefined,
      title: undefined
    }
  }

  const queryParams = new URLSearchParams(location?.search || '')
  const forceReauthAll =
    (queryParams.get('forceReauth') || queryParams.get('force_reauth') || '').toLowerCase() ===
    'all'

  const forceReauthCodes = (localStorage?.getItem('debug_force_reauth_codes') || '')
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean)

  const backendReauthFlag =
    platform?.is_reauth_required ??
    platform?.platform_info?.is_reauth_required ??
    platform?.credentials?.is_reauth_required

  const isNonOauthPlatform = platform?.platform_info?.isOauth === false
  const nonOauthReauthFlag = isNonOauthPlatform && platform?.is_reauth_required === true
  const oauthReauthFlag = !isNonOauthPlatform && !!backendReauthFlag
  const oauthExpiringSoonReauthFlag =
    platform?.platform_info?.isOauth === true &&
    daysUntilExpiration !== null &&
    daysUntilExpiration <= 7

  const legacyReauthCase =
    ['JUMIAVENDOR'].includes(platform?.code || '') &&
    !!platform?.credentials?.is_token_expiry_notified

  const isVipulUser =
    (fetchedUserAccountData?.email || '').toLowerCase() === 'vipul.constacloud@gmail.com'
  const isEbay = (platform?.code || '').toUpperCase() === 'EBAY'
  const vipulEbayForceReauth = isVipulUser && isEbay && forceReauthAll

  const shouldShowReauthButton =
    oauthReauthFlag ||
    oauthExpiringSoonReauthFlag ||
    nonOauthReauthFlag ||
    legacyReauthCase ||
    forceReauthAll ||
    forceReauthCodes.includes((platform?.code || '').toUpperCase()) ||
    vipulEbayForceReauth

  return (
    <div className='card shadow p-2 my-3'>
      <div className='d-sm-flex flex-md-row justify-content-between p-8 border border-light-primary rounded position-relative'>
        {(platform?.isMainStore ||
          platform?.isAccountingStore ||
          platform?.isShippingStore ||
          platform?.isErpStore ||
          platform?.isOmsStore) && (
          <div className='position-absolute top-0 start-0 p-2'>
            <span
              className='badge rounded-pill bg-primary'
              style={{fontWeight: 600, fontSize: '11px'}}
            >
              {platform?.isMainStore
                ? 'Source Channel'
                : platform?.isShippingStore
                ? 'Shipping Store'
                : platform?.isAccountingStore
                ? 'Accounting Store'
                : platform?.isErpStore
                ? 'ERP Store'
                : platform?.isOmsStore
                ? 'OMS Store'
                : 'Destination Channel'}
            </span>
          </div>
        )}
        <div className='d-flex flex-row align-self-center text-align-center'>
          <img
            src={platform.platform_info.logo_ref}
            alt='Channel Logo'
            className='me-2'
            style={{maxWidth: '50px', height: '30px', objectFit: 'contain'}}
          />
          <h4 className='title mt-1'>
            {platform?.platform_info?.display_name + ' - ' + platform.display_name}
          </h4>
        </div>
        <div className='d-md-inline-flex d-flex  mt-sm-0 mt-4 justify-content-sm-start justify-content-between'>
          <div className='d-flex align-items-center'>
            {/* Platform Connection Status */}
            <span
              className={`fs-5 fw-bold mx-2 ${
                ['JUMIAVENDOR'].includes(platform?.code!) &&
                platform.credentials?.is_token_expiry_notified
                  ? 'text-danger'
                  : platform.isConnected
                  ? 'text-success'
                  : 'text-danger'
              }`}
              style={{height: '35px', alignContent: 'center'}}
            >
              {['JUMIAVENDOR'].includes(platform?.code!) &&
              platform.credentials?.is_token_expiry_notified
                ? 'Not Connected'
                : platform.isConnected
                ? 'Connected'
                : 'Not Connected'}
            </span>

            {/* // Refresh Token (temporarily hidden)
            {['LAZADA', 'ETSY', 'EBAY'].includes(platform?.code!) &&
            daysUntilExpiration !== null &&
            daysUntilExpiration <= 7 ? (
              <button
                disabled={isRefreshing}
                className='btn btn-sm bg-light-primary text-primary shadow-sm fs-6 fw-bold ms-3'
                onClick={onTokenRefresh}
              >
                {isRefreshing && ['LAZADA', 'ETSY', 'EBAY'].includes(platform?.code!)
                  ? 'Refreshing...'
                  : 'Refresh Token'}
              </button>
            ) : null}
            */}

            {/* // Re-auth / reconnect */}
            {shouldShowReauthButton ? (
              <button
                disabled={isRefreshing}
                className='btn btn-sm bg-light-primary text-primary shadow-sm fs-6 fw-bold ms-3'
                onClick={handleOauth}
              >
                {isRefreshing ? 'Re-authenticating...' : 'Re-auth'}
              </button>
            ) : null}
            {/* Admin-only beta cog icon removed per request */}

            {/* showing Dynamic Platform Setting from  AIMS360 and admin only */}
            {/* {(['AIMS360'].includes(platform.code) ||
              localStorage.getItem(loggedInAdminAccessToken)) && (
              <OverlayTrigger
                placement='top'
                delay={{show: 250, hide: 400}}
                overlay={(props) => (
                  <Tooltip id='button-tooltip' {...props}>
                    Dynamic Platform Setting
                  </Tooltip>
                )}
              >
                <Link
                  to={`/platform-setting-new/${platform.platform_info.id}/${platform.id}`}
                  className='me-0 mx-sm-2'
                >
                  <KTSVG
                    path='media/icons/duotune/coding/cod001.svg'
                    className='svg-icon-muted svg-icon-2hx p-2 '
                  />
                </Link>
              </OverlayTrigger>
            )} */}

            {/* <Link to={`/platform-setting-new/${platform.platform_info.id}/${platform.id}`} className='btn btn-sm me-0 me-sm-2 '> */}
            {
              // !['AIMS360','BIGCOMMERCE'].includes(platform.code) &&
              <Link
                to={`/channel-settings/${platform.platform_info.id}/${platform.id}`}
                className='me-0 mx-sm-2'
              >
                <KTSVG
                  path='media/icons/duotune/coding/cod009.svg'
                  className='svg-icon-muted svg-icon-2hx p-2'
                />
              </Link>
            }
            {(isDelete && canShowDeleteButton() ) && (
              <button 
                className='btn btn-sm btn-danger' 
                {...getDeleteButtonProps()} 
                onClick={onDelete}
              >
              {/* <KTSVG
                path='media/icons/duotune/general/gen034.svg'
                className='svg-icon-muted svg-icon-2hx'
              /> */}
              <KTIcon iconName='trash' className=' fs-3' />
            </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConnectPlatformStrip

